<template>
  <div class="bi-control-panel-table-container pr-5">
    <v-card>
      <v-data-table
        class="bi-control-panel-table bi-control-panel-table--parameters"
        :style="tableMaxHeightStyle"
        item-key="id"
        :headers="headers"
        :items="biParameterData"
        :loading="tableLoading"
        mobile-breakpoint="0"
        fixed-header
        hide-default-footer
        hide-default-header
        disable-pagination
        dense
      >
        <template #header="{ props }">
          <thead class="v-data-table-header">
            <tr>
              <th
                v-for="header in props.headers"
                :key="header.value"
                :class="header.class"
              >
                <div class="d-flex align-center">
                  <v-tooltip bottom>
                    <template #activator="{ on, attrs }">
                      <span v-bind="attrs" v-on="on" class="bi-header-text">{{ header.text }}</span>
                    </template>
                    <span>{{ header.text }}</span>
                  </v-tooltip>
                </div>
              </th>
            </tr>
          </thead>
        </template>

        <template #item.financialGroup="{ item }">
          <v-tooltip bottom :disabled="!item.financialGroup">
            <template #activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">{{ item.financialGroup }}</span>
            </template>
            <span>{{ item.financialGroup }}</span>
          </v-tooltip>
        </template>

        <template #item.contract="{ item }">
          <v-tooltip bottom :disabled="!item.contract">
            <template #activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">{{ item.contract }}</span>
            </template>
            <span>{{ item.contract }}</span>
          </v-tooltip>
        </template>

        <template #item.subcontract="{ item }">
          <v-tooltip bottom :disabled="!item.subcontract">
            <template #activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">{{ item.subcontract }}</span>
            </template>
            <span>{{ item.subcontract }}</span>
          </v-tooltip>
        </template>

        <template #item.carrier="{ item }">
          <v-tooltip bottom :disabled="!item.carrier">
            <template #activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">{{ item.carrier }}</span>
            </template>
            <span>{{ item.carrier }}</span>
          </v-tooltip>
        </template>

        <template #item.competency="{ item }">
          <v-tooltip bottom :disabled="!item.competency">
            <template #activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">{{ formatCompetency(item.competency) }}</span>
            </template>
            <span>{{ formatCompetency(item.competency) }}</span>
          </v-tooltip>
        </template>

        <template #item.invoiceValue="{ item }">
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on" class="bi-control-panel-money-cell">
                {{ formatCurrency(item.invoiceValue) }}
              </span>
            </template>
            <span>{{ formatCurrency(item.invoiceValue) }}</span>
          </v-tooltip>
        </template>

        <template #item.approvalStatus="{ item }">
          <div class="d-flex align-center bi-control-panel-status-cell">
            <v-switch
              v-if="canEdit"
              :input-value="item.approvalStatus"
              color="primary"
              hide-details
              inset
              dense
              :disabled="isStatusUpdating(item.id)"
              :loading="isStatusUpdating(item.id)"
              class="status-switch mr-2 mt-0 pt-0"
              @change="updateStatus(item, $event)"
            />
            <span>
              {{ item.approvalStatus ? $t('biControlPanel.table.active') : $t('biControlPanel.table.inactive') }}
            </span>
          </div>
        </template>

        <template #item.updateDate="{ item }">
          <v-tooltip bottom :disabled="!item.updateDate">
            <template #activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">{{ item.updateDate }}</span>
            </template>
            <span>{{ item.updateDate }}</span>
          </v-tooltip>
        </template>

        <template #item.updateUser="{ item }">
          <v-tooltip bottom :disabled="!item.updateUser">
            <template #activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">{{ item.updateUser }}</span>
            </template>
            <span>{{ item.updateUser }}</span>
          </v-tooltip>
        </template>

        <template #body.append>
          <tr
            v-if="biParameterData.length"
            class="summary-row"
          >
            <td colspan="5" class="text-right font-weight-medium">
            </td>
            <td class="font-weight-bold text-right">
              {{ invoiceTotalFormatted }}
            </td>
            <td colspan="3"></td>
          </tr>
        </template>
      </v-data-table>
    </v-card>
    <SnackbarCustomize ref="SnackbarCustomize" />
  </div>
</template>

<script>
import moment from 'moment';
import SnackbarCustomize from '@/components/CustomAlerts/SnackbarCustomize.vue';
import BIControlPanelModule from '@/store/modules/bi-control-panel/bi-control-panel-module';

export default {
  name: 'BIParametersTable',
  components: { SnackbarCustomize },
  props: {
    canEdit: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currencyFormatter: new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }),
      updatingStatusIds: [],
      wrapperMaxHeight: null,
    };
  },
  computed: {
    tableState() {
      return BIControlPanelModule.parametersTable;
    },
    biParameterData() {
      return this.tableState.rows;
    },
    tableLoading() {
      return this.tableState.loading;
    },
    tableMaxHeightStyle() {
      if (!this.wrapperMaxHeight) return null;

      return { '--bi-table-max-height': `${this.wrapperMaxHeight}px` };
    },
    invoiceTotal() {
      return this.biParameterData.reduce(
        (total, row) => (row.approvalStatus
          ? total + this.normalizeCurrencyValue(row.invoiceValue)
          : total),
        0,
      );
    },
    invoiceTotalFormatted() {
      return this.formatCurrency(this.invoiceTotal);
    },
    headers() {
      return [
        {
          text: this.translate('biControlPanel.filters.financialGroup'),
          value: 'financialGroup',
          class: 'col-financial-group',
          cellClass: 'col-financial-group',
        },
        {
          text: this.translate('biControlPanel.filters.contract'),
          value: 'contract',
          class: 'col-contract',
          cellClass: 'col-contract',
        },
        {
          text: this.translate('biControlPanel.filters.subcontract'),
          value: 'subcontract',
          class: 'col-subcontract',
          cellClass: 'col-subcontract',
        },
        {
          text: this.translate('biControlPanel.filters.carrier'),
          value: 'carrier',
          class: 'col-carrier',
          cellClass: 'col-carrier',
        },
        {
          text: this.translate('biControlPanel.filters.competency'),
          value: 'competency',
          class: 'col-competency',
          cellClass: 'col-competency',
        },
        {
          text: this.translate('biControlPanel.table.invoiceValue'),
          value: 'invoiceValue',
          align: 'end',
          class: 'col-invoice-value text-right',
          cellClass: 'col-invoice-value text-right',
        },
        {
          text: this.translate('biControlPanel.table.status'),
          value: 'approvalStatus',
          class: 'col-status',
          cellClass: 'col-status',
        },
        {
          text: this.translate('biControlPanel.table.lastUpdateDate'),
          value: 'updateDate',
          class: 'col-update-date',
          cellClass: 'col-update-date',
        },
        {
          text: this.translate('biControlPanel.table.updateUser'),
          value: 'updateUser',
          class: 'col-update-user',
          cellClass: 'col-update-user',
        },
      ];
    },
  },
  watch: {
    biParameterData() {
      this.$nextTick(() => {
        this.updateWrapperMaxHeight();
      });
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.updateWrapperMaxHeight();
    });
    window.addEventListener('resize', this.updateWrapperMaxHeight);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateWrapperMaxHeight);
  },
  methods: {
    updateWrapperMaxHeight() {
      const wrapper = this.$el?.querySelector('.v-data-table__wrapper');

      if (!wrapper) return;

      const { top } = wrapper.getBoundingClientRect();
      const height = window.innerHeight - top - 20;

      this.wrapperMaxHeight = Math.max(240, Math.floor(height));
    },
    normalizeCurrencyValue(value) {
      if (typeof value === 'number') {
        return Number.isFinite(value) ? value : 0;
      }

      if (typeof value === 'string') {
        const normalized = value
          .replace(/\./g, '')
          .replace(',', '.')
          .replace(/[^\d.-]/g, '');
        const parsedValue = Number(normalized);

        return Number.isFinite(parsedValue) ? parsedValue : 0;
      }

      return 0;
    },
    formatCurrency(value) {
      return this.currencyFormatter.format(this.normalizeCurrencyValue(value));
    },
    formatCompetency(value) {
      if (!value) {
        return '';
      }

      const competency = moment(value, 'YYYY-MM', true);

      if (!competency.isValid()) {
        return value;
      }

      return competency.format('MM/YYYY');
    },
    isStatusUpdating(id) {
      return this.updatingStatusIds.includes(id);
    },
    setStatusUpdating(id, loading) {
      if (loading) {
        if (!this.isStatusUpdating(id)) {
          this.updatingStatusIds = [...this.updatingStatusIds, id];
        }
        return;
      }

      this.updatingStatusIds = this.updatingStatusIds.filter((itemId) => itemId !== id);
    },
    updateStatus(item, value) {
      const nextStatus = Boolean(value);

      if (item.approvalStatus === nextStatus || this.isStatusUpdating(item.id)) {
        return;
      }

      this.setStatusUpdating(item.id, true);
      this.runStoreAction(
        BIControlPanelModule.updateParametersStatus({
          id: item.id,
          approvalStatus: nextStatus,
        }),
        this.translate('biControlPanel.messages.statusUpdateError'),
      ).finally(() => {
        this.setStatusUpdating(item.id, false);
      });
    },
    translate(key) {
      return this.$t(key).toString();
    },
    showError(message) {
      this.$refs.SnackbarCustomize.open('error', message);
    },
    async runStoreAction(actionPromise, fallbackMessage) {
      const result = await actionPromise;

      if (result && result.success === false) {
        this.showError(result.errorMessage || fallbackMessage);
      }

      return result;
    },
  },
};
</script>

<style lang="scss">
@import "@/shared/styles/biControlPanel/table.scss";
</style>
